/**
 * 动态Voronoi图验证配置
 * 为新 ValidationEngine 提供验证规则
 */

import type { ValidationConfig } from '../../../core/dataProcessor/types';

// 🆕 为新 ValidationEngine 导出的配置
export const dynamicVoronoiValidationConfig: ValidationConfig = {
  fields: [
    { field: 'x', type: 'string', required: true },
    { field: 'y', type: 'number', required: true },
    { field: 'group', type: ['string', 'date'], required: true }
  ],
  businessRules: [
    {
      name: 'dynamic-voronoi-sufficient-data',
      validator: data => Array.isArray(data) && data.length > 1,
      message: 'Dynamic Voronoi requires more than 1 data point',
      suggestion: 'Provide at least 2 data points for meaningful Voronoi diagram visualization',
      severity: 'error'
    },
    {
      name: 'dynamic-voronoi-multiple-groups',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }
        const { encoding } = layer;
        const groups = new Set(data.map(item => item[encoding?.group as string]));
        return groups.size > 1;
      },
      message: 'Dynamic Voronoi requires multiple time points or groups',
      suggestion: 'Provide data from multiple time points or groups for dynamic visualization',
      severity: 'error'
    },
    {
      name: 'dynamic-voronoi-multiple-categories',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }
        const { encoding } = layer;
        const categories = new Set(data.map(item => item[encoding?.x as string]));
        return categories.size > 1;
      },
      message: 'Dynamic Voronoi requires multiple categories',
      suggestion: 'Provide multiple categories in the x field for spatial distribution analysis',
      severity: 'warning'
    },
    {
      name: 'dynamic-voronoi-valid-numbers',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }
        const { encoding } = layer;
        return data.every(item => {
          const value = item[encoding?.y as string];
          return typeof value === 'number' && !Number.isNaN(value) && Number.isFinite(value);
        });
      },
      message: 'Dynamic Voronoi y values must be valid numbers',
      suggestion: 'Ensure all y field values are valid, finite numbers',
      severity: 'error'
    },
    {
      name: 'dynamic-voronoi-positive-values',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }
        const { encoding } = layer;
        return data.every(item => {
          const value = item[encoding?.y as string];
          return typeof value === 'number' && value >= 0;
        });
      },
      message: 'Dynamic Voronoi works best with non-negative values',
      suggestion: 'Consider using non-negative values for better visualization results',
      severity: 'warning'
    }
  ]
};
